import React from "react";
import { useState, useEffect, useRef } from "react";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/SOLAR-NFT11-removebg-preview.png";
import { hasPermission } from "../utils/permissionUtils";
import { useSelector } from "react-redux";

const Navbar = () => {
  const [nav, setNav] = useState(false);
  const [historyDropdown, setHistoryDropdown] = useState(false);
  const [reportDropdown, setReportDropdown] = useState(false);
  const navigate = useNavigate();
  const userData = useSelector((state) => state.auth.data);
  
  // Create refs for dropdown containers
  const historyRef = useRef(null);
  const reportRef = useRef(null);

  const isAdmin = () => {
    const role = localStorage.getItem("role");
    return role === "admin";
  };

  const historyLinks = [
    {
      id: 1,
      link: "Stake History",
      url: "/order-history",
      permission: { resource: "stake_history", action: "read" },
    },
    {
      id: 2,
      link: "Transaction History",
      url: "/transaction",
      permission: { resource: "transaction_history", action: "read" },
    },
    {
      id: 3,
      link: "ROI History",
      url: "/roi_history",
      permission: { resource: "roi_history", action: "read" },
    },
  ];

  const reportLinks = [
    {
      id: 1,
      link: "Transaction Report",
      url: "/report-trans",
      permission: { resource: "report", action: "read" },
    },
  ];

  const links = [
    {
      id: 1,
      link: "Dashboard",
      url: "/",
      permission: { resource: "user_details", action: "read" },
    },
    {
      id: 2,
      link: "Referal",
      url: "/referal",
      permission: { resource: "referal", action: "read" },
    },
    {
      id: 3,
      link: "Subadmin",
      url: "/subadmin",
      showIfAdmin: true,
    },
    {
      id: 4,
      link: "Settings",
      url: "/settings",
      showIfAdmin: true,
    }
  ];

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (historyRef.current && !historyRef.current.contains(event.target)) {
        setHistoryDropdown(false);
      }
      if (reportRef.current && !reportRef.current.contains(event.target)) {
        setReportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close other dropdown when one is opened
  useEffect(() => {
    if (historyDropdown) {
      setReportDropdown(false);
    }
  }, [historyDropdown]);

  useEffect(() => {
    if (reportDropdown) {
      setHistoryDropdown(false);
    }
  }, [reportDropdown]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('permission');
    localStorage.removeItem('role');
    navigate('/login');
  };

  const shouldShowHistoryLink = (link) => {
    if (isAdmin()) {
      return true;
    }
    
    return !link.permission || hasPermission(link?.permission?.resource, link?.permission?.action);
  };

  const shouldShowLink = (link) => {
    if (link.showIfAdmin) {
      return isAdmin();
    }
    
    if (isAdmin()) {
      return true;
    }
    
    return !link.permission || hasPermission(link?.permission?.resource, link?.permission?.action);
  };

  const hasAnyHistoryPermission = () => {
    if (isAdmin()) return true;
    return historyLinks.some(link => shouldShowHistoryLink(link));
  };

  const shouldShowReportLink = (link) => {
    if (isAdmin()) {
      return true;
    }
    
    return !link.permission || hasPermission(link?.permission?.resource, link?.permission?.action);
  };

  const hasAnyReportPermission = () => {
    if (isAdmin()) return true;
    return reportLinks.some(link => shouldShowReportLink(link));
  };

  return (
    <div className="flex justify-between items-center w-full h-16 px-4 bg-blue-100 z-50">
      <Link to="/">
        <img src={logo} className="h-12 mix-blend-darken" alt="Logo" />
      </Link>
      <ul className="hidden md:flex mx-auto gap-8">
        {/* Dashboard first */}
        {(() => {
          const dashboard = links.find(l => l.link === "Dashboard");
          if (dashboard && shouldShowLink(dashboard)) {
            return (
              <li key={dashboard.id} className="relative px-4 cursor-pointer capitalize font-bold text-blue-900 hover:scale-105 duration-200">
                <Link to={dashboard.url}>{dashboard.link}</Link>
              </li>
            );
          }
          return null;
        })()}

        {/* History second */}
        {hasAnyHistoryPermission() && (
          <li ref={historyRef} className="relative px-4 cursor-pointer capitalize font-bold text-blue-900 hover:scale-105 duration-200">
            <div 
              className="flex items-center gap-1"
              onClick={() => setHistoryDropdown(!historyDropdown)}
            >
              History
              <FaChevronDown className={`transition-transform duration-200 ${historyDropdown ? 'rotate-180' : ''}`} />
            </div>
            
            {historyDropdown && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                {historyLinks.map((link) => {
                  if (shouldShowHistoryLink(link)) {
                    return (
                      <Link
                        key={link.id}
                        to={link.url}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                        onClick={() => setHistoryDropdown(false)}
                      >
                        {link.link}
                      </Link>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </li>
        )}

        {/* Report third */}
        {hasAnyReportPermission() && (
          <li ref={reportRef} className="relative px-4 cursor-pointer capitalize font-bold text-blue-900 hover:scale-105 duration-200">
            <div 
              className="flex items-center gap-1"
              onClick={() => setReportDropdown(!reportDropdown)}
            >
              Report
              <FaChevronDown className={`transition-transform duration-200 ${reportDropdown ? 'rotate-180' : ''}`} />
            </div>
            
            {reportDropdown && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                {reportLinks.map((link) => {
                  if (shouldShowReportLink(link)) {
                    return (
                      <Link
                        key={link.id}
                        to={link.url}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                        onClick={() => setReportDropdown(false)}
                      >
                        {link.link}
                      </Link>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </li>
        )}

        {/* Then Referal, Subadmin, Settings */}
        {links
          .filter(l => l.link !== "Dashboard")
          .map((link) => {
            if (shouldShowLink(link)) {
              return (
                <li
                  key={link.id}
                  className="relative px-4 cursor-pointer capitalize font-bold text-blue-900 hover:scale-105 duration-200"
                >
                  <Link to={link.url}>{link.link}</Link>
                </li>
              );
            }
            return null;
          })}
      </ul>

      {/* User info and logout section */}
      <div className="hidden md:flex items-center gap-4">
        <button
          onClick={handleLogout}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
        >
          Logout
        </button>
      </div>

      <div
        onClick={() => setNav(!nav)}
        className="cursor-pointer pr-4 z-20 text-blue-900 md:hidden"
      >
        {nav ? <FaTimes size={30} /> : <FaBars size={30} />}
      </div>

      {nav && (
        <ul className="flex flex-col justify-center items-center absolute z-10 top-0 left-0 w-full h-screen bg-white">
          {/* Dashboard first */}
          {(() => {
            const dashboard = links.find(l => l.link === "Dashboard");
            if (dashboard && shouldShowLink(dashboard)) {
              return (
                <li key={dashboard.id} className="px-4 cursor-pointer capitalize py-6 text-2xl" onClick={() => setNav(!nav)}>
                  <Link to={dashboard.url} smooth duration={800}>
                    {dashboard.link}
                  </Link>
                </li>
              );
            }
            return null;
          })()}

          {/* History second in mobile */}
          {hasAnyHistoryPermission() && (
            <>
              <li className="px-4 py-6 text-2xl font-bold text-blue-900">
                History
              </li>
              {historyLinks.map((link) => {
                if (shouldShowHistoryLink(link)) {
                  return (
                    <li
                      key={link.id}
                      className="px-8 cursor-pointer capitalize py-3 text-xl text-gray-600"
                      onClick={() => setNav(!nav)}
                    >
                      <Link
                        to={link.url}
                        smooth
                        duration={800}
                      >
                        {link.link}
                      </Link>
                    </li>
                  );
                }
                return null;
              })}
            </>
          )}

          {/* Report third in mobile */}
          {hasAnyReportPermission() && (
            <>
              <li className="px-4 py-6 text-2xl font-bold text-blue-900">
                Report
              </li>
              {reportLinks.map((link) => {
                if (shouldShowReportLink(link)) {
                  return (
                    <li
                      key={link.id}
                      className="px-8 cursor-pointer capitalize py-3 text-xl text-gray-600"
                      onClick={() => setNav(!nav)}
                    >
                      <Link
                        to={link.url}
                        smooth
                        duration={800}
                      >
                        {link.link}
                      </Link>
                    </li>
                  );
                }
                return null;
              })}
            </>
          )}
          
          {/* Then Referal, Subadmin, Settings */}
          {links
            .filter(l => l.link !== "Dashboard")
            .map((link) => {
              if (shouldShowLink(link)) {
                return (
                  <li
                    key={link.id}
                    className="px-4 cursor-pointer capitalize py-6 text-2xl"
                    onClick={() => setNav(!nav)}
                  >
                    <Link to={link.url} smooth duration={800}>
                      {link.link}
                    </Link>
                  </li>
                );
              }
              return null;
            })}
          
          <li className="px-4 py-6 text-2xl">
            <span className="text-blue-900 font-semibold">
              {userData?.username || 'User'}
            </span>
          </li>
          <li
            className="px-4 py-6 text-2xl"
            onClick={() => {
              handleLogout();
              setNav(!nav);
            }}
          >
            <button className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors">
              Logout
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

export default Navbar;